import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useModuleDependencies } from '../../hooks/subscriptions/useModuleDependencies';

interface ModuleDependenciesCellProps {
  moduleKey: string;
}

/**
 * Component to display module dependencies in a table cell
 * Fetches and displays the actual module names that this module depends on
 */
export const ModuleDependenciesCell = ({ moduleKey }: ModuleDependenciesCellProps) => {
  const { data: dependencies, isLoading } = useModuleDependencies(moduleKey, true);

  if (isLoading) {
    return (
      <Chip
        label="Loading..."
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    );
  }

  if (!dependencies || dependencies.length === 0) {
    return (
      <Chip
        label="None"
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    );
  }

  // If only 1-2 dependencies, show them inline
  if (dependencies.length <= 2) {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {dependencies.map((dep) => (
          <Tooltip key={dep.id} title={dep.description} arrow>
            <Chip
              label={dep.name}
              size="small"
              color="info"
              sx={{
                fontSize: '0.75rem',
                height: '22px',
                backgroundColor: '#1976d220',
                color: '#1976d2',
                fontWeight: 500,
              }}
            />
          </Tooltip>
        ))}
      </Stack>
    );
  }

  // If 3+ dependencies, show count with tooltip
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
            Requires:
          </Typography>
          {dependencies.map((dep) => (
            <Typography key={dep.id} variant="caption" display="block">
              • {dep.name}
            </Typography>
          ))}
        </Box>
      }
      arrow
    >
      <Chip
        label={`${dependencies.length} required`}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    </Tooltip>
  );
};
